import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { BookOpen, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

/**
 * Automatic engineering explanation shown UNDER a generated plan.
 * Bilingual (AR/EN); consumes POST /editor/explain.
 */
export const PlanExplanation = ({ model, lang = "ar" }) => {
  const isAr = (lang || "ar").toLowerCase().startsWith("ar");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!model || !model.walls) return;
    let alive = true;
    setLoading(true); setErr(null);
    api.editorExplainPlan(model)
      .then((r) => { if (alive) setData(r); })
      .catch((e) => { if (alive) setErr(String(e?.message || e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [model]);

  if (!model || !model.walls) return null;

  return (
    <div className="rounded-2xl border border-border bg-card/60 p-5" data-testid="plan-explanation">
      <div className="mb-3 flex items-center gap-2">
        <BookOpen className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-bold">{isAr ? "شرح المخطط الهندسي" : "Engineering explanation"}</h3>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="plan-explanation-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> {isAr ? "جارٍ تحليل المخطط…" : "Analysing the plan…"}
        </div>
      )}
      {err && !loading && (
        <p className="text-sm text-destructive">{isAr ? "تعذّر توليد الشرح" : "Could not build the explanation"} - {err}</p>
      )}

      {data && !loading && (
        <div className="space-y-4">
          <p className="text-sm font-bold text-primary" data-testid="plan-explanation-summary">
            {isAr ? data.summary_ar : data.summary_en}
          </p>

          {data.stats && (
            <div className="flex flex-wrap gap-2">
              <Stat label={isAr ? "فراغات" : "Spaces"} value={data.stats.rooms} />
              <Stat label={isAr ? "أعمدة" : "Columns"} value={data.stats.columns} />
              <Stat label={isAr ? "مناور" : "Light-wells"} value={data.stats.light_wells} />
              <Stat label={isAr ? "المساحة" : "Area"} value={`${data.stats.plot_area_m2} م²`} />
              <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold ${data.stats.circulation_ok ? "bg-emerald-500/10 text-emerald-400" : "bg-amber-500/10 text-amber-400"}`}>
                {data.stats.circulation_ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
                {isAr ? (data.stats.circulation_ok ? "حركة سليمة" : "راجع الحركة") : (data.stats.circulation_ok ? "Circulation OK" : "Check circulation")}
              </span>
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            {(data.sections || []).map((s, i) => (
              <div key={i} className="rounded-xl border border-border/60 bg-background/40 p-3">
                <p className="mb-1 text-sm font-bold">{isAr ? s.title_ar : s.title_en}</p>
                <p className="text-sm leading-relaxed text-muted-foreground">{isAr ? s.body_ar : s.body_en}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background/50 px-3 py-1 text-xs font-bold">
    <span className="text-muted-foreground">{label}</span>
    <span className="text-foreground">{value}</span>
  </span>
);

export default PlanExplanation;
